import {
  ArrowPathIcon,
  DocumentPlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  ChangesSummary,
  CreatedEntityPattern,
  EventRow,
  StatusChangePattern,
  Tag,
  UserActionPattern,
} from '@/components/domain-events-v2/shared';
import { RegistryDefinitions } from '@/components/domain-events-v2/types';
import { CoverageLink } from '@/components/entity-links';
import { CoverageSchema } from '@/services/api/generated/matterEventsService';

const getCoverageDisplayName = (coverage: CoverageSchema): string => {
  const carrierName = coverage?.carrier?.name;
  const category = coverage?.category;

  let categoryShorthand = '';
  switch (category?.toUpperCase()) {
    case 'FIRST_PARTY':
      categoryShorthand = '1P';
      break;
    case 'THIRD_PARTY':
      categoryShorthand = '3P';
      break;
    case 'FIRST_PARTY_HEALTH':
      categoryShorthand = '1P Health';
      break;
    default:
      categoryShorthand = 'Coverage';
  }

  if (carrierName) {
    return `${carrierName} (${categoryShorthand})`;
  }

  return categoryShorthand;
};

const linkClass =
  'font-medium text-gray-900 hover:text-blue-600 transition-colors';

export const coverageEvents: RegistryDefinitions = {
  coverage_created: {
    icon: DocumentPlusIcon,
    iconColor: 'text-purple-500',
    priority: 'high',
    render: (event) => {
      const { payload } = event;
      const displayName = getCoverageDisplayName(payload.coverage);
      return (
        <CreatedEntityPattern
          event={event}
          entityType="coverage"
          entityName={
            <CoverageLink
              entityId={payload.coverage.id}
              matterId={event.matter}
              className={linkClass}
            >
              {displayName}
            </CoverageLink>
          }
          details={
            payload.needs_approval ? (
              <EventRow>
                <Tag color="amber" size="xs">
                  Needs Review
                </Tag>
              </EventRow>
            ) : undefined
          }
        />
      );
    },
    description: (event) => {
      const p = event.payload;
      return `added coverage: ${getCoverageDisplayName(p.coverage)}`;
    },
  },

  coverage_updated: {
    icon: PencilIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const displayName = getCoverageDisplayName(payload.coverage);
      return (
        <UserActionPattern
          event={event}
          action="updated"
          details={
            payload.diff ? (
              <ChangesSummary diff={payload.diff as Record<string, unknown>} />
            ) : undefined
          }
        >
          <CoverageLink
            entityId={payload.coverage.id}
            matterId={event.matter}
            className={linkClass}
          >
            {displayName}
          </CoverageLink>
        </UserActionPattern>
      );
    },
    description: () => 'updated coverage',
  },

  coverage_status_changed: {
    icon: ArrowPathIcon,
    iconColor: 'text-gray-400',
    priority: 'low',
    render: (event) => {
      const { payload } = event;
      const displayName = getCoverageDisplayName(payload.coverage);
      return (
        <StatusChangePattern
          event={event}
          entityType="coverage"
          entityName={
            <CoverageLink
              entityId={payload.coverage.id}
              matterId={event.matter}
              className={linkClass}
            >
              {displayName}
            </CoverageLink>
          }
          oldStatus={payload.old_status}
          newStatus={payload.new_status}
          customVerb={
            payload.new_status?.toLowerCase() === 'approved'
              ? 'approved'
              : undefined
          }
        />
      );
    },
    description: (event) => {
      const p = event.payload;
      return `changed coverage status to ${p.new_status}`;
    },
  },
};
